import { type QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import type { Question } from '@/features/jobs/questions/questions'
import { UnknownQuestionAnswerEditorViewModel } from '@/features/jobs/questions/unknown/UnknownQuestionAnswerEditorViewModel'
import { BoolQuestionAnswerEditorViewModel } from '@/features/jobs/questions/bool/BoolQuestionAnswerEditorViewModel'
import { SelectQuestionAnswerEditorViewModel } from '@/features/jobs/questions/select/SelectQuestionAnswerEditorViewModel'
import { DateTimeQuestionAnswerEditorViewModel } from '@/features/jobs/questions/date-time/DateTimeQuestionAnswerEditorViewModel'
import { NumericQuestionAnswerEditorViewModel } from '@/features/jobs/questions/numeric/NumericQuestionAnswerEditorViewModel'
import { TextQuestionAnswerEditorViewModel } from '@/features/jobs/questions/text/TextQuestionAnswerEditorViewModel'
import { BreakerQuestionAnswerEditorViewModel } from '@/features/jobs/questions/breaker/BreakerQuestionAnswerEditorViewModel'

/**
 * Factory for creating question answer editors.
 */
export interface QuestionAnswerEditorViewModelFactory {
  createAnswerEditorViewModel<T extends Question>(
    question: T,
  ): QuestionAnswerEditorViewModelBase<Question>
}

/**
 * Options for the default factory.
 */
export interface DefaultQuestionAnswerEditorViewModelFactoryOptions {
  /**
   * Gets the "as-of" date to use for checking date ranges.
   */
  getAsOfDate: () => Date

  /**
   * Navigates backward.
   */
  goBack: () => unknown
}

/**
 * Default implementation.
 */
export class DefaultQuestionAnswerEditorViewModelFactory implements QuestionAnswerEditorViewModelFactory {
  constructor(
    private readonly _options: DefaultQuestionAnswerEditorViewModelFactoryOptions,
  ) {}

  /** @inheritDoc */
  createAnswerEditorViewModel<T extends Question>(
    question: T,
  ): QuestionAnswerEditorViewModelBase<T> {
    let vm
    switch (question.type) {
      case 'Bool': {
        vm = new BoolQuestionAnswerEditorViewModel()
        break
      }
      case 'Select': {
        vm = new SelectQuestionAnswerEditorViewModel()
        break
      }
      case 'DateTime': {
        vm = new DateTimeQuestionAnswerEditorViewModel(
          this._options.getAsOfDate,
          // Doesn't have to be reactive for now.
          () => new Date(),
        )
        break
      }
      case 'Numeric': {
        vm = new NumericQuestionAnswerEditorViewModel()
        break
      }
      case 'Text': {
        vm = new TextQuestionAnswerEditorViewModel()
        break
      }
      case 'Breaker': {
        vm = new BreakerQuestionAnswerEditorViewModel(this._options.goBack)
        break
      }
      case 'Unknown':
      default: {
        vm = new UnknownQuestionAnswerEditorViewModel()
      }
    }

    return vm as QuestionAnswerEditorViewModelBase<T>
  }
}
